import ThemedSelect from "../components/shared/ThemedSelect.jsx";
import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import RulesHelp from "../components/shared/RulesHelp.jsx";
import { myobMailtoHref } from "../utils/myob.js";
import {
  addOfficeExpenseNote,
  createOfficeExpense,
  deleteOfficeExpense,
  deleteOfficeExpenseFile,
  errorMessage,
  getCurrentUser,
  isAbortError,
  listEntityRows,
  listOfficeExpenseCategories,
  listOfficeExpenseNotes,
  listOfficeExpenses,
  officeExpenseFileUrl,
  officeExpensesCsvUrl,
  setOfficeExpenseStage,
  updateOfficeExpense,
  uploadOfficeExpenseFile,
} from "../services/apiClient.js";
import { formatDate, todayInput } from "../utils/date.js";

// Office Expenses — GPS's own supplier bills with an approval trail
// (docs/business-rules/office-expenses.md). KR kept this inside job 69773 for
// three years, logging ~700 bills as contractor invoices so the approve →
// accounts → paid trail existed somewhere. This is that flow as its own page:
// a register, not a job. Admin only, the way the route is.
//
// Shape: a year strip (the accountant's unit) with totals, the filtered list,
// inline add/edit like every grid here, the trail as one button per stage,
// notes under a bill or on the register itself, and a CSV for accounts.

const nzd = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" });
const money = (v) => nzd.format(Number(v || 0));

const STATUS = {
  received: { label: "Received", bg: "secondary" },
  approved: { label: "Approved", bg: "info" },
  with_accounts: { label: "Sent for payment", bg: "warning" },
  paid: { label: "Paid", bg: "success" },
};

const EMPTY = {
  expense_date: "", supplier_company_id: "", supplier_name: "", invoice_number: "",
  category: "", description: "", amount: "",
};

function OfficeExpensesPage() {
  const thisYear = Number(todayInput().slice(0, 4));
  const [filters, setFilters] = useState({ year: String(thisYear), status: "", supplier_id: "", q: "" });
  const [data, setData] = useState(null); // { rows, totals, years }
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [editingId, setEditingId] = useState(null); // null | "new" | id
  const [form, setForm] = useState(EMPTY);
  const [formError, setFormError] = useState("");
  // Anyone sees and adds; only an Administrator works the trail (Approve, Sent
  // for payment), so the buttons only show for them — the route refuses the
  // rest regardless.
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    let live = true;
    getCurrentUser().then((u) => { if (live) setIsAdmin(!!u?.is_admin); }).catch(() => {});
    return () => { live = false; };
  }, []);
  // Which field a refusal is about — the message alone, at the foot of the
  // entry row, read as if it were about whatever box was last typed in
  // (Robert 2026-09-05: "invoice number must be a number", which was the
  // Amount box talking).
  const [invalidField, setInvalidField] = useState(null);
  const [error, setError] = useState("");
  const [notesFor, setNotesFor] = useState(null); // null | id | "register"
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [reload, setReload] = useState(0);
  // One hidden file input serves every row: clicking a File cell remembers
  // which bill, the browser's picker does the rest. Dropping onto the cell
  // skips the picker.
  const fileInputRef = useRef(null);
  const [attachFor, setAttachFor] = useState(null);
  const [dragOver, setDragOver] = useState(null); // row id under a dragged file

  // Every fetch in an effect gets an AbortController (architecture.md §11):
  // a superseded filter change must not paint over the newer answer.
  useEffect(() => {
    const controller = new AbortController();
    setError("");
    listOfficeExpenses(
      { year: filters.year || undefined, status: filters.status || undefined,
        supplier_id: filters.supplier_id || undefined, q: filters.q || undefined },
      controller.signal
    )
      .then(setData)
      .catch((err) => { if (!isAbortError(err)) setError(errorMessage(err, "Unable to load the register.")); });
    return () => controller.abort();
  }, [filters, reload]);

  useEffect(() => {
    const controller = new AbortController();
    listOfficeExpenseCategories(controller.signal).then(setCategories).catch(() => {});
    listEntityRows("contractor-companies").then(setSuppliers).catch(() => {});
    return () => controller.abort();
  }, [reload]);

  useEffect(() => {
    if (notesFor == null) return undefined;
    const controller = new AbortController();
    listOfficeExpenseNotes(notesFor === "register" ? null : notesFor, controller.signal)
      .then(setNotes)
      .catch((err) => { if (!isAbortError(err)) setError(errorMessage(err, "Unable to load notes.")); });
    return () => controller.abort();
  }, [notesFor, reload]);

  const set = (patch) => {
    setForm((c) => ({ ...c, ...patch }));
    if (invalidField && patch[invalidField] !== undefined) {
      setInvalidField(null);
      setFormError("");
    }
  };
  const setFilter = (patch) => setFilters((c) => ({ ...c, ...patch }));
  const refresh = () => setReload((n) => n + 1);

  // The year strip always covers the whole register, plus this year even
  // when it is empty so a new year has somewhere to land.
  const years = useMemo(() => {
    const list = [...(data?.years || [])];
    if (!list.some((y) => y.year === thisYear)) list.unshift({ year: thisYear, count: 0, amount: 0, unsent_count: 0, unsent_amount: 0 });
    return list;
  }, [data, thisYear]);

  function startAdd() {
    setFormError("");
    setForm({ ...EMPTY, expense_date: todayInput() });
    setEditingId("new");
  }
  function startEdit(row) {
    setFormError("");
    setForm({
      expense_date: String(row.expense_date).slice(0, 10),
      supplier_company_id: row.supplier_company_id || "",
      supplier_name: row.supplier_company_id ? "" : row.supplier_name || "",
      invoice_number: row.invoice_number || "",
      category: row.category || "",
      description: row.description || "",
      amount: row.amount ?? "",
    });
    setEditingId(row.id);
  }
  async function save() {
    setFormError("");
    setInvalidField(null);
    // The same three rules the server holds, said next to the box concerned.
    if (!form.expense_date) {
      setFormError("The bill needs a date."); setInvalidField("expense_date"); return;
    }
    if (!form.supplier_company_id && !form.supplier_name.trim()) {
      setFormError("Who is the bill from? Pick a supplier or type one."); setInvalidField("supplier_name"); return;
    }
    if (String(form.amount).trim() === "" || !Number.isFinite(Number(form.amount))) {
      setFormError("Amount must be a number — the invoice number goes in Invoice #."); setInvalidField("amount"); return;
    }
    try {
      if (editingId === "new") {
        await createOfficeExpense(form);
        // Next bill straight away — the accounts pile comes in batches.
        setForm({ ...EMPTY, expense_date: form.expense_date });
      } else {
        await updateOfficeExpense(editingId, form);
        setEditingId(null);
      }
      refresh();
    } catch (err) {
      setFormError(errorMessage(err, "Unable to save the bill."));
    }
  }
  async function stage(row, next) {
    setError("");
    try {
      await setOfficeExpenseStage(row.id, next);
      refresh();
    } catch (err) {
      setError(errorMessage(err, "Unable to update the bill."));
    }
  }
  async function remove(row) {
    if (!window.confirm(`Delete the ${money(row.amount)} bill from ${row.supplier || "this supplier"}?`)) return;
    setError("");
    try {
      await deleteOfficeExpense(row.id);
      refresh();
    } catch (err) {
      setError(errorMessage(err, "Unable to delete the bill."));
    }
  }
  async function saveNote() {
    setError("");
    try {
      await addOfficeExpenseNote(notesFor === "register" ? null : notesFor, noteText);
      setNoteText("");
      refresh();
    } catch (err) {
      setError(errorMessage(err, "Unable to add the note."));
    }
  }
  // A bill is ONE document (the supplier's bill): attaching to a row that has
  // one REPLACES it — the new file lands first, so a failed upload never
  // costs the old one (Robert 2026-09-05: a drop target that replaces).
  async function putFile(row, file) {
    if (!file) return;
    setError("");
    try {
      await uploadOfficeExpenseFile(row.id, file);
      for (const old of row.files) await deleteOfficeExpenseFile(row.id, old.id);
      refresh();
    } catch (err) {
      setError(errorMessage(err, "Unable to attach the file."));
    }
  }
  function attach(row) {
    setAttachFor(row.id);
    fileInputRef.current?.click();
  }
  async function onFilePicked(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    const row = rows.find((r) => r.id === attachFor);
    setAttachFor(null);
    if (file && row) await putFile(row, file);
  }
  const dragProps = (row) => ({
    onDragOver: (e) => { e.preventDefault(); if (dragOver !== row.id) setDragOver(row.id); },
    onDragLeave: () => setDragOver((cur) => (cur === row.id ? null : cur)),
    onDrop: (e) => { e.preventDefault(); setDragOver(null); putFile(row, e.dataTransfer.files?.[0]); },
  });
  async function removeFile(row, f) {
    if (!window.confirm(`Remove "${f.name}" from this bill?`)) return;
    setError("");
    try {
      await deleteOfficeExpenseFile(row.id, f.id);
      refresh();
    } catch (err) {
      setError(errorMessage(err, "Unable to remove the file."));
    }
  }

  const supplierOptions = suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>);
  const rows = data?.rows || [];

  // The File cell IS the control: a drop target that is also a click target.
  // Empty = "drop the bill here"; with a file = the link, a quiet ×, and a
  // drop replaces.
  function fileCell(row) {
    const over = dragOver === row.id;
    const f = row.files[0];
    return (
      <td
        {...dragProps(row)}
        className={over ? "bg-primary-subtle" : undefined}
        style={{ cursor: "pointer", borderStyle: over ? "dashed" : undefined }}
        data-hint={f ? "Drop a file here to replace it, or click to choose one" : "Drop the bill here, or click to choose a file"}
        onClick={(e) => { if (e.target.closest("a, [data-remove]")) return; attach(row); }}
      >
        {f ? (
          <span className="text-nowrap">
            <a href={officeExpenseFileUrl(row.id, f.id)} data-hint={`${f.name} (${Math.max(1, Math.round(Number(f.size_bytes) / 1024))} KB)`}>
              📎 {f.name}
            </a>
            <span role="button" data-remove className="text-muted ms-1" data-hint="Remove this file" onClick={() => removeFile(row, f)}>×</span>
          </span>
        ) : (
          <span className="text-muted">{over ? "Drop to attach" : "Drop the bill here"}</span>
        )}
      </td>
    );
  }

  function editorRow(key) {
    return (
      <tr key={key} className="bg-body-tertiary">
        <td colSpan={9}>
          {/* Fields in the TABLE's column order — Date, Supplier, Invoice #,
              Category, What for, Amount — so nothing invites the invoice
              number into the Amount box. Invoice # is free text: "WP-3679131",
              "9502/99885802" and "AP3349" are all real ones. */}
          <Row className="g-2 align-items-end">
            <Col xs={6} md={2}>
              <Form.Label className="mb-0 small fw-semibold">Date<span className="text-danger"> *</span></Form.Label>
              <Form.Control size="sm" type="date" value={form.expense_date} autoFocus
                isInvalid={invalidField === "expense_date"}
                onChange={(e) => set({ expense_date: e.target.value })} />
            </Col>
            <Col xs={6} md={4}>
              <Form.Label className="mb-0 small fw-semibold">Supplier<span className="text-danger"> *</span></Form.Label>
              <ThemedSelect size="sm" value={form.supplier_company_id}
                isInvalid={invalidField === "supplier_name" && !form.supplier_company_id}
                onChange={(e) => set({ supplier_company_id: e.target.value, ...(e.target.value ? { supplier_name: "" } : {}) })}>
                <option value="">— Not a listed contractor (type below) —</option>
                {supplierOptions}
              </ThemedSelect>
              {!form.supplier_company_id ? (
                <Form.Control size="sm" className="mt-1" placeholder="Supplier name" value={form.supplier_name}
                  isInvalid={invalidField === "supplier_name"}
                  onChange={(e) => set({ supplier_name: e.target.value })} />
              ) : null}
            </Col>
            <Col xs={6} md={3}>
              <Form.Label className="mb-0 small fw-semibold">Invoice #</Form.Label>
              <Form.Control size="sm" value={form.invoice_number} placeholder="as printed on the bill"
                onChange={(e) => set({ invoice_number: e.target.value })} />
            </Col>
            <Col xs={6} md={3}>
              <Form.Label className="mb-0 small fw-semibold">Category</Form.Label>
              <Form.Control size="sm" list="office-expense-categories" value={form.category}
                onChange={(e) => set({ category: e.target.value })} />
              <datalist id="office-expense-categories">
                {categories.map((c) => <option key={c} value={c} />)}
              </datalist>
            </Col>
            <Col xs={12} md={7}>
              <Form.Label className="mb-0 small fw-semibold">What for</Form.Label>
              <Form.Control size="sm" value={form.description} onChange={(e) => set({ description: e.target.value })} />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="mb-0 small fw-semibold">Amount<span className="text-danger"> *</span></Form.Label>
              <Form.Control size="sm" type="number" step="0.01" className="text-end" value={form.amount}
                isInvalid={invalidField === "amount"}
                onChange={(e) => set({ amount: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && save()} />
            </Col>
            <Col xs={6} md={3} className="d-flex gap-1 justify-content-end">
              <Button size="sm" onClick={save}>{key === "new" ? "Add bill" : "Save"}</Button>
              <Button size="sm" variant="secondary" onClick={() => setEditingId(null)}>{key === "new" ? "Done" : "Cancel"}</Button>
            </Col>
            {formError ? <Col xs={12}><div className="text-danger small">{formError}</div></Col> : null}
          </Row>
        </td>
      </tr>
    );
  }

  function trailButtons(row) {
    const b = (label, next, variant = "outline-primary", title = "") => (
      <Button key={next} size="sm" variant={variant} className="me-1" data-hint={title} onClick={() => stage(row, next)}>{label}</Button>
    );
    // The trail KR works: Approve → Email MYOB → Sent for payment, and that is
    // the end of it — nobody here knows "paid" without asking, so no button
    // offers it. The paid stamp exists for the imported history and for MYOB
    // later; such a bill shows Paid and has nothing left to press.
    // The email is 1.0's Orders-screen link, verbatim (Karen Sweatman
    // 2026-09-07: "once she has approved she emails it to that myob address"):
    // it opens a mail to MYOB's import inbox and she attaches the bill.
    switch (row.status) {
      case "received":
        return b("Approve", "approved", "outline-success", "GPS says pay it — stamps you and now");
      case "approved":
        return (
          <>
            <a
              href={myobMailtoHref()}
              className="btn btn-sm btn-outline-primary me-1"
              data-hint="Opens an email to accounts' MYOB importer — attach the bill, then press Sent for payment"
            >
              Email MYOB
            </a>
            {b("Sent for payment", "with_accounts", "outline-success", "Emailed to MYOB / handed to accounts. Done, as far as this register can know.")}
            {b("Un-approve", "unapproved", "outline-secondary")}
          </>
        );
      case "with_accounts":
        return b("Back", "not_with_accounts", "outline-secondary", "Not sent after all");
      default:
        return null;
    }
  }

  function notesRow(row) {
    return (
      <tr key={`notes-${row.id}`}>
        <td colSpan={9} className="bg-body-tertiary">
          <div className="small fw-semibold mb-1">Notes on this bill</div>
          {notes.length === 0 ? <div className="small text-muted mb-2">No notes yet.</div> : null}
          {notes.map((n) => (
            <div key={n.id} className="small mb-1" style={{ whiteSpace: "pre-wrap" }}>
              <span className="text-muted">{formatDate(n.created_at)}{n.author ? ` · ${n.author}` : ""}: </span>{n.note}
            </div>
          ))}
          <div className="d-flex gap-1 mt-2">
            <Form.Control size="sm" placeholder="Add a note — sent to accounts, chased, etc." value={noteText}
              onChange={(e) => setNoteText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveNote()} />
            <Button size="sm" onClick={saveNote}>Add</Button>
            <Button size="sm" variant="secondary" onClick={() => setNotesFor(null)}>Close</Button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
        <h4 className="mb-0">Office Expenses <RulesHelp topic="officeExpenses" /></h4>
        <span className="small text-muted">GPS's own supplier bills — received, approved, sent for payment, paid.</span>
      </div>

      {/* The year strip: the whole register, one button per year. */}
      <div className="d-flex flex-wrap gap-2 mb-3">
        {years.map((y) => (
          <Button
            key={y.year}
            size="sm"
            variant={String(y.year) === filters.year ? "primary" : "outline-primary"}
            onClick={() => setFilter({ year: String(y.year) })}
            data-hint={`${y.count} bill${y.count === 1 ? "" : "s"} · ${money(y.amount)} · ${y.unsent_count} not yet sent for payment (${money(y.unsent_amount)})`}
          >
            {y.year}
            <span className="ms-2 small opacity-75">{y.count} · {money(y.amount)}</span>
            {y.unsent_count > 0 ? <Badge bg="warning" text="dark" className="ms-2">{y.unsent_count} not sent</Badge> : null}
          </Button>
        ))}
        <Button size="sm" variant={filters.year === "" ? "primary" : "outline-primary"} onClick={() => setFilter({ year: "" })}>
          All years
        </Button>
      </div>

      <Row className="g-2 align-items-end mb-3">
        <Col xs={6} md={2}>
          <Form.Label className="mb-0 small fw-semibold">Status</Form.Label>
          <ThemedSelect size="sm" value={filters.status} onChange={(e) => setFilter({ status: e.target.value })}>
            <option value="">All</option>
            {Object.entries(STATUS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
          </ThemedSelect>
        </Col>
        <Col xs={6} md={3}>
          <Form.Label className="mb-0 small fw-semibold">Supplier</Form.Label>
          <ThemedSelect size="sm" value={filters.supplier_id} onChange={(e) => setFilter({ supplier_id: e.target.value })}>
            <option value="">All</option>
            {supplierOptions}
          </ThemedSelect>
        </Col>
        <Col xs={12} md={3}>
          <Form.Label className="mb-0 small fw-semibold">Search</Form.Label>
          <Form.Control size="sm" placeholder="supplier, invoice #, category, what for" value={filters.q}
            onChange={(e) => setFilter({ q: e.target.value })} />
        </Col>
        <Col className="d-flex gap-2 justify-content-end align-items-center">
          {data ? (
            <span className="small text-muted text-nowrap">
              {data.totals.count} bill{data.totals.count === 1 ? "" : "s"} · <strong>{money(data.totals.amount)}</strong>
            </span>
          ) : null}
          <a className="btn btn-sm btn-outline-secondary" href={officeExpensesCsvUrl({ year: filters.year, status: filters.status, supplier_id: filters.supplier_id, q: filters.q })}>
            Download CSV
          </a>
          <Button size="sm" variant="outline-secondary" onClick={() => { setNotesFor(notesFor === "register" ? null : "register"); setNoteText(""); }}>
            Register notes
          </Button>
          {editingId === null ? <Button size="sm" variant="success" onClick={startAdd}>+ Add bill</Button> : null}
        </Col>
      </Row>

      {error ? <Alert variant="danger" className="py-2" dismissible onClose={() => setError("")}>{error}</Alert> : null}
      <input ref={fileInputRef} type="file" hidden onChange={onFilePicked} />

      {notesFor === "register" ? (
        <Card className="mb-3">
          <Card.Body className="py-2">
            <div className="small fw-semibold mb-1">Register notes — about the bills in general</div>
            {notes.length === 0 ? <div className="small text-muted mb-2">No notes yet.</div> : null}
            {notes.map((n) => (
              <div key={n.id} className="small mb-1" style={{ whiteSpace: "pre-wrap" }}>
                <span className="text-muted">{formatDate(n.created_at)}{n.author ? ` · ${n.author}` : ""}: </span>{n.note}
              </div>
            ))}
            <div className="d-flex gap-1 mt-2">
              <Form.Control size="sm" placeholder="Add a note" value={noteText}
                onChange={(e) => setNoteText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveNote()} />
              <Button size="sm" onClick={saveNote}>Add</Button>
              <Button size="sm" variant="secondary" onClick={() => setNotesFor(null)}>Close</Button>
            </div>
          </Card.Body>
        </Card>
      ) : null}

      <Table bordered hover responsive size="sm" className="align-middle">
        <thead className="table-surface">
          <tr>
            <th style={{ width: 100 }}>Date</th>
            <th>Supplier</th>
            <th style={{ width: 120 }}>Invoice #</th>
            <th style={{ width: 140 }}>Category</th>
            <th>What for</th>
            <th style={{ width: 220 }}>File</th>
            <th className="text-end" style={{ width: 110 }}>Amount</th>
            <th style={{ width: 120 }}>Status</th>
            <th className="text-end" style={{ width: 360 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {editingId === "new" ? editorRow("new") : null}
          {rows.flatMap((row) => {
            if (editingId === row.id) return [editorRow(row.id)];
            const out = [
              <tr key={row.id}>
                <td className="text-nowrap">{formatDate(row.expense_date)}</td>
                <td>{row.supplier || <span className="text-muted">—</span>}</td>
                <td>{row.invoice_number || <span className="text-muted">—</span>}</td>
                <td>{row.category || <span className="text-muted">—</span>}</td>
                <td style={{ whiteSpace: "pre-wrap" }}>{row.description || <span className="text-muted">—</span>}</td>
                {fileCell(row)}
                <td className="text-end text-nowrap fw-semibold">{money(row.amount)}</td>
                <td>
                  <Badge bg={STATUS[row.status].bg} text={row.status === "with_accounts" ? "dark" : undefined}
                    data-hint={[
                      row.approved_at ? `Approved ${formatDate(row.approved_at)}${row.approved_by_name ? " by " + row.approved_by_name : ""}` : null,
                      row.sent_to_accounts_at ? `Sent for payment ${formatDate(row.sent_to_accounts_at)}` : null,
                      row.paid_on ? `Paid ${formatDate(row.paid_on)}` : null,
                    ].filter(Boolean).join(" · ") || "Received, nothing stamped yet"}>
                    {STATUS[row.status].label}
                  </Badge>
                </td>
                <td className="text-end text-nowrap">
                  {isAdmin ? trailButtons(row) : null}
                  <Button size="sm" variant="outline-secondary" className="me-1"
                    onClick={() => { setNotesFor(notesFor === row.id ? null : row.id); setNoteText(""); }}>
                    Notes{row.note_count ? ` (${row.note_count})` : ""}
                  </Button>
                  <Button size="sm" variant="outline-primary" className="me-1" onClick={() => startEdit(row)}>Edit</Button>
                  {row.status === "received" || row.status === "approved" ? (
                    <Button size="sm" variant="outline-danger" onClick={() => remove(row)}>Delete</Button>
                  ) : null}
                </td>
              </tr>,
            ];
            if (notesFor === row.id) out.push(notesRow(row));
            return out;
          })}
          {data && rows.length === 0 && editingId !== "new" ? (
            <tr><td colSpan={9} className="text-center text-muted">No bills match.</td></tr>
          ) : null}
          {!data && !error ? (
            <tr><td colSpan={9} className="text-center text-muted">Loading…</td></tr>
          ) : null}
        </tbody>
      </Table>
    </div>
  );
}

export default OfficeExpensesPage;
